// src/hooks/useChatScroll.js
// Chat scrolling that behaves: sticks to the bottom for new messages only if
// you're already there (or you just sent one); otherwise leaves your place
// alone and offers a "jump to latest" button. resetKey = which conversation.
import { useRef, useState, useEffect } from 'react'

export default function useChatScroll(messages, myId, resetKey, typing = false) {
  const containerRef = useRef(null)
  const bottomRef    = useRef(null)
  const nearBottom   = useRef(true)
  const lastCount    = useRef(0)
  const [showJump, setShowJump] = useState(false)

  useEffect(() => { lastCount.current = 0; setShowJump(false) }, [resetKey])

  function onScroll() {
    const el = containerRef.current
    if (!el) return
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (nearBottom.current) setShowJump(false)
  }

  useEffect(() => {
    const grew = messages.length > lastCount.current
    const firstLoad = lastCount.current === 0
    lastCount.current = messages.length
    if (!grew) return
    const last = messages[messages.length - 1]
    if (firstLoad || nearBottom.current || last?.sender_id === myId) {
      bottomRef.current?.scrollIntoView({ behavior: firstLoad ? 'auto' : 'smooth' })
      setShowJump(false)
    } else {
      setShowJump(true)
    }
  }, [messages, myId])

  // Keep the "typing…" line in view, but only if you're already at the bottom
  useEffect(() => {
    if (typing && nearBottom.current) bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [typing])

  function jumpToLatest() {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShowJump(false)
  }

  return { containerRef, bottomRef, onScroll, showJump, jumpToLatest }
}
